import { CheckIcon } from 'lucide-react';

import type { DimeAutocompleteOption } from '../lib/dime-autocomplete-option.js';

type DimeFilterListRowProps = Readonly<{
  id: string;
  option: Readonly<DimeAutocompleteOption>;
  checked: boolean;
  /** The row the keyboard is on. */
  active: boolean;
  onSelect: () => void;
  onPointerMove: () => void;
}>;

/** One row of a filter list: `aria-selected` is its tick, as screen readers read a multi-select listbox, and `data-active` the row the keyboard is on. */
export function DimeFilterListRow({
  id,
  option,
  checked,
  active,
  onSelect,
  onPointerMove,
}: DimeFilterListRowProps) {
  const disabled = option.disabled === true;
  // A disabled row says why under its name, where the description would sit.
  const description = disabled ? (option.disabledReason ?? option.description) : option.description;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- keys reach rows through the list's aria-activedescendant, so the row itself never takes focus.
    <div
      id={id}
      role="option"
      aria-selected={checked}
      aria-disabled={disabled}
      data-checked={checked}
      data-active={active || undefined}
      // A panel of lists keeps an active row in each, so only the list holding focus shows its own.
      className="group/filter-row relative flex cursor-default items-center gap-2 rounded-md py-1 pr-2 pl-1.5 text-sm outline-hidden select-none hover:bg-muted aria-disabled:pointer-events-none aria-disabled:opacity-50 group-focus-within/filter-list:data-active:bg-muted group-focus-within/filter-list:data-active:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      onPointerMove={disabled ? undefined : onPointerMove}
      onClick={disabled ? undefined : onSelect}
    >
      {option.icon != null && (
        <span className="shrink-0 text-muted-foreground" aria-hidden="true">
          {option.icon}
        </span>
      )}
      {description ? (
        <span className="flex flex-col">
          <span>{option.label}</span>
          <span className="text-xs text-muted-foreground">{description}</span>
        </span>
      ) : (
        <span>{option.label}</span>
      )}
      <CheckIcon
        aria-hidden
        className="ml-auto opacity-0 group-data-[checked=true]/filter-row:opacity-100"
      />
    </div>
  );
}
